import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'

import { TemplateEditor, type TemplateMode } from './template-editor'

export interface TemplateFormData {
  name: string
  slug: string
  subject: string
  body: string
  mode: TemplateMode
}

export interface TemplateFormFieldsProps {
  data: TemplateFormData
  onChange: (field: keyof TemplateFormData, value: string) => void
  /**
   * When true, the slug field is disabled — used on edit pages to enforce
   * "slugs are immutable post-create" because consuming services hardcode
   * them and a rename would silently 404.
   */
  slugLocked?: boolean
}

export function TemplateFormFields({ data, onChange, slugLocked }: TemplateFormFieldsProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-1.5">
        <label htmlFor="templateName" className="text-sm font-medium text-muted-foreground">
          Name
        </label>
        <Input id="templateName" value={data.name} onChange={(e) => onChange('name', e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="templateSlug" className="text-sm font-medium text-muted-foreground">
          Slug
        </label>
        <Input
          id="templateSlug"
          value={data.slug}
          onChange={(e) => onChange('slug', e.target.value)}
          disabled={slugLocked}
          placeholder="auto-generated from name"
        />
        <p className="text-xs text-muted-foreground">
          {slugLocked
            ? 'Slugs are locked after creation — consuming services hardcode them.'
            : 'Stable identifier callers use to reference this template. Lowercase, digits, and dashes only. Auto-fills from name; edit to override.'}
        </p>
      </div>
      <div className="space-y-1.5">
        <label htmlFor="templateSubject" className="text-sm font-medium text-muted-foreground">
          Subject
        </label>
        <Input
          id="templateSubject"
          value={data.subject}
          onChange={(e) => onChange('subject', e.target.value)}
          placeholder="e.g. Reset your Ordo password"
        />
        <p className="text-xs text-muted-foreground">
          {
            'Default subject for email sends using this template. Variables work like the body — e.g. {{contact.firstName}}. A send that supplies its own subject overrides this.'
          }
        </p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-muted-foreground">Template Body</span>
          <Select value={data.mode} onValueChange={(v) => onChange('mode', v)}>
            <SelectTrigger className="h-7 w-auto gap-1 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="plaintext">Plain Text</SelectItem>
              <SelectItem value="html">HTML</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <TemplateEditor value={data.body} onChange={(v) => onChange('body', v)} mode={data.mode} />
      </div>
    </div>
  )
}
